/**
 * The API client.
 *
 * Cookies are httpOnly and carried by the browser; nothing here touches a token,
 * because there is no token to mishandle.
 */

import type { NxView } from './nx-sources.ts';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body: unknown = null,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      /*
       * 🔴 Only when there IS a body. Fastify refuses a request that announces
       * `application/json` and then sends nothing — "Body cannot be empty when
       * content-type is set to 'application/json'" — and the API's error
       * handler turns any unexpected status into a bare 500, so the screen
       * showed "internal error" for a DELETE the server never even reached.
       *
       * It applied to every `api.del` call, which means the routing screen's
       * Remove button had never worked in a browser either. Both suites test
       * those routes through `app.inject`, which sends no content-type at all
       * and so cannot see this. Found by pressing the button; see
       * `admin_api_client.test.ts` for the guard that would have found it.
       */
      ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(init.headers ?? {}),
    },
  });

  const text = await response.text();
  const body: unknown = text === '' ? null : JSON.parse(text);

  if (!response.ok) {
    const message =
      typeof body === 'object' && body !== null && 'error' in body
        ? String((body as { error: unknown }).error)
        : response.statusText;
    throw new ApiError(response.status, message, body);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path),
  post: <T>(path: string, payload?: unknown): Promise<T> =>
    request<T>(path, { method: 'POST', body: JSON.stringify(payload ?? {}) }),
  // `request` stringifies nothing on its own — the bug that made four buttons
  // in the predecessor silently do nothing was a caller passing an object
  // straight through, so every verb that carries a body does it here.
  put: <T>(path: string, payload?: unknown): Promise<T> =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(payload ?? {}) }),
  /** Named `del` because `delete` is a reserved word. */
  del: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),
};

// ── Shapes the server actually returns ────────────────────────────────────

export interface Me {
  user: { id: string; email: string; display_name: string | null; is_platform_admin: boolean };
  tenantId: string | null;
  /** The tenant the session is in, by name and the caller's role in it. */
  tenant: { id: string; slug: string; name: string; role: string } | null;
  mfaSatisfied: boolean;
}

export interface LoginResult {
  userId: string;
  mfaRequired: boolean;
  reasons: string[];
}

export interface MfaStatus {
  required: boolean;
  enrolled: boolean;
  satisfiedForSession: boolean;
  reasons: string[];
  recoveryCodesRemaining: number;
}

export interface TenantOption {
  id: string;
  slug: string;
  name: string;
  role: string;
  require_mfa: boolean;
}

export interface Site {
  id: string;
  code: string | null;
  name: string;
  timezone: string;
  locality: string | null;
  region: string | null;
}

export interface Asset {
  id: string;
  site_id: string | null;
  parent_asset_id: string | null;
  kind: string;
  driver_id: string | null;
  name: string;
  code: string | null;
  lifecycle: string;
  /**
   * Is this deployment meant to record onto the device's own storage?
   *
   * `assets.records_onboard` — see `fleet-state.ts`, which reads it. Only
   * `/api/fleet` returns it today; anything else building an `Asset` by hand
   * should leave it true, which is the column's default.
   */
  records_onboard: boolean;
  /** What NX calls this camera (070), when NX records it; only `/api/fleet` sends it. */
  nx_name?: string | null;
  /** NX's status for it, from the same list. */
  nx_status?: string | null;
  /** On a failover server (072): the NX server it is normally on; null when it is there. */
  nx_failover_home?: string | null;
}

export interface CheckStateRow {
  asset_id: string;
  check_key: string;
  outcome: 'pass' | 'fail' | 'unknown' | 'unavailable';
  reason: string | null;
  value_num: number | null;
  value_text: string | null;
  since: string;
  last_checked: string;
  last_definite: string | null;
}

export interface AlertRow {
  id: string;
  asset_id: string;
  class_key: string;
  cause_key: string;
  cause_label: string;
  operator_action: string | null;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  raised_at: string;
  /**
   * Who has said they have this, and when.
   *
   * 🔴 Acknowledged is not resolved. The alert stays open and the engine still
   * resolves it on evidence — see the route comment in `src/api/server.ts`.
   * A screen that renders an acknowledged alert as handled would be making a
   * product decision nobody has taken.
   */
  ack_at: string | null;
  ack_by: string | null;
  ack_by_name: string | null;
}

/**
 * One line of the activity timeline.
 *
 * `source` is load-bearing rather than decorative: an `event` is something the
 * SYSTEM observed and an `audit` row is something a PERSON did. They come from
 * separate tables on purpose — see the route comment — and a row that could not
 * say which it was would recreate the inversion those tables exist to prevent.
 */
export interface ActivityRow {
  source: 'event' | 'audit';
  at: string;
  action: string;
  summary: string;
  asset_id: string | null;
  asset_name: string | null;
  asset_code: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
  target_kind: string | null;
  detail: Record<string, unknown> | null;
}

export interface ActivityResponse {
  activity: ActivityRow[];
  /** `truncated` so a full page is not read as the beginning of the record. */
  window: { limit: number; truncated: boolean };
}

export interface NotificationRow {
  id: string;
  alert_id: string | null;
  channel_id: string | null;
  routing_key: string | null;
  /** `held` is its own outcome: owed a digest, as distinct from a chosen silence. */
  status: 'sent' | 'failed' | 'held' | 'suppressed';
  reason: string | null;
  at: string;
  channel_label: string | null;
  channel_kind: string | null;
}

export interface NotificationsResponse {
  notifications: NotificationRow[];
  tally: { sent: number; failed: number; held: number; suppressed: number };
  window: { limit: number; truncated: boolean };
}

export interface CoverageDay {
  asset_id: string;
  asset_code: string | null;
  asset_name: string | null;
  recorder_key: string;
  day: string;
  covered_ms: string | number;
  window_ms: string | number;
  gap_count: number;
  longest_gap_ms: string | number;
  verdict: 'measured' | 'indeterminate';
  unparsed_count: number;
  empty_count: number;
  observed_at: string;
  /** null when the day could not be measured — never coerce this to 0. */
  pct: string | null;
}

export interface CoverageResponse {
  days: CoverageDay[];
  /**
   * Four counts rather than one percentage, because every way of folding an
   * indeterminate day into a single figure is a lie: counting it as covered
   * hides a camera nobody could read, counting it as a gap invents an outage,
   * and dropping it changes the denominator silently.
   */
  summary: { full: number; partial: number; none: number; indeterminate: number };
  window: { days: number };
}

export interface SnapshotMeta {
  asset_id: string;
  byte_length: number;
  captured_at: string;
  via: string;
}

/**
 * A site, plus where the server put it in the fault ranking.
 *
 * The rank is computed in SQL by `/api/fleet` — see the query there for the
 * tuple and why it is not a boolean. It comes down rather than being recomputed
 * here because the ORDER is the server's, and a screen that decides for itself
 * which sites are quiet would be a second opinion about the list it was handed.
 */
export interface FleetSite extends Site {
  /** Assets in active service. The only ones the site is judged on. */
  rank_active: number;
  /** Of those, how many are not answering. */
  rank_down: number;
  /** Of those, how many a person would act on — every fault plus every warning. */
  rank_affected: number;
  /** 2 = something is faulted, 1 = only warnings, 0 = nothing open. */
  rank_severity: 0 | 1 | 2;
  /**
   * Every asset in active service here is not answering: one site failure —
   * power, a router, a link — rather than N camera faults.
   */
  rank_dark: boolean;
  /** A holding pen rather than a place. `UNASSIGNED`; see the server. */
  rank_placeless: boolean;
}

export interface Fleet {
  sites: FleetSite[];
  assets: Asset[];
  checkState: CheckStateRow[];
  alerts: AlertRow[];
  snapshots: SnapshotMeta[];
}

// ── One asset, in detail ──────────────────────────────────────────────────

export interface DetailCheck {
  check_key: string;
  outcome: 'pass' | 'fail' | 'unknown' | 'unavailable';
  reason: string | null;
  value_num: number | null;
  value_text: string | null;
  since: string;
  last_checked: string;
  last_definite: string | null;
  label: string;
  description: string | null;
  stale_after_s: number;
  cost: string;
  requires: string;
}

export interface DetailObservation {
  check_key: string;
  outcome: 'pass' | 'fail' | 'unknown' | 'unavailable';
  reason: string | null;
  value_num: number | null;
  observed_at: string;
  latency_ms: number | null;
}

export interface DetailAlert {
  id: string;
  class_key: string;
  cause_key: string;
  cause_label: string;
  operator_action: string | null;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  raised_at: string;
  resolved_at: string | null;
  evidence: Record<string, unknown> | null;
}

export interface DetailCapability {
  key: string;
  label: string;
  description: string | null;
  declared: boolean;
}

export interface AssetDetail {
  asset: {
    id: string;
    name: string;
    code: string | null;
    kind: string;
    driver_id: string | null;
    lifecycle: string;
    notes: string | null;
    created_at: string;
    config: Record<string, unknown>;
    site_id: string | null;
    site_code: string | null;
    site_name: string | null;
    timezone: string | null;
  };
  checks: DetailCheck[];
  /** NX's view (070) when NX records this camera; null otherwise. See nx-sources.ts. */
  nx?: NxView | null;
  observations: DetailObservation[];
  alerts: DetailAlert[];
  capabilities: DetailCapability[];
  /**
   * What each recorder was last observed to hold for this camera.
   *
   * Empty means one of two different things and the screen must not merge them:
   * this camera is on no recorder, or nothing has looked. `backup_reconcile_runs`
   * is what tells those apart and it is platform-scoped, so this route cannot
   * see it — which is why `observed_at` comes back raw and staleness is judged
   * on the screen.
   */
  backups: DetailBackup[];
}

export interface DetailBackup {
  recorder_key: string;
  registered: boolean;
  enabled: boolean;
  writing_state: string;
  writing_state_since: string;
  observed_at: string;
}
